import React from 'react';
import styled from 'styled-components';

/* ------------------------------------------------------------------ */
/*  Layout primitives                                                  */
/* ------------------------------------------------------------------ */

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif;
  color: #1c1c1a;
  -webkit-font-smoothing: antialiased;
`;

const Stack = styled.div`
  width: 100%;
  max-width: 380px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Card = styled.section`
  background: #fffefb;
  border-radius: 22px;
  padding: 20px 20px 18px;
  box-shadow:
    0 1px 2px rgba(28, 25, 23, 0.05),
    0 12px 32px rgba(28, 25, 23, 0.08);
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const RowBetween = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

/* ------------------------------------------------------------------ */
/*  Card 1 - holdings breakdown (segmented bar)                        */
/* ------------------------------------------------------------------ */

const LegendRow = styled.div`
  display: flex;
  gap: 18px;
  margin: 12px 0 10px;
`;

const LegendItem = styled.div`
  font-size: 13.5px;
  color: #6f6d66;
  white-space: nowrap;

  strong {
    color: #1c1c1a;
    font-weight: 600;
    margin-right: 6px;
  }
`;

const SegBar = styled.div`
  display: flex;
  gap: 3px;
  height: 6px;
`;

const Seg = styled.div<{ w: number; c: string }>`
  width: ${(p) => p.w}%;
  background: ${(p) => p.c};
  border-radius: 99px;
`;

/* ------------------------------------------------------------------ */
/*  Card 2 & 3 - gauge with marker                                     */
/* ------------------------------------------------------------------ */

const BigNumber = styled.div`
  font-size: 34px;
  font-weight: 600;
  letter-spacing: -0.03em;
  line-height: 1;
  text-align: right;

  small {
    font-size: 14px;
    font-weight: 500;
    color: #8f8d85;
    letter-spacing: 0;
  }
`;

const BigCaption = styled.div`
  font-size: 12.5px;
  color: #8f8d85;
  text-align: right;
  margin-top: 6px;
`;

const Gauge = styled.div`
  position: relative;
  margin: 30px 2px 8px;
`;

const Track = styled.div`
  position: relative;
  height: 4px;
  border-radius: 99px;
  background: #edebe5;
`;

const Band = styled.div<{ l: number; w: number; c?: string }>`
  position: absolute;
  left: ${(p) => p.l}%;
  width: ${(p) => p.w}%;
  top: 0;
  bottom: 0;
  background: ${(p) => p.c || '#34c19a'};
  border-radius: 99px;
`;

const Marker = styled.div<{ at: number }>`
  position: absolute;
  left: ${(p) => p.at}%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;

  &::before {
    content: '';
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid #1c1c1a;
    margin-bottom: 5px;
  }

  &::after {
    content: '';
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: #fff;
    border: 2.5px solid #1c1c1a;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  }
`;

const TickRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 10.5px;
  color: #b3b0a7;
`;

/* ------------------------------------------------------------------ */
/*  Card 4 - review list (pills + sparklines)                          */
/* ------------------------------------------------------------------ */

const DateText = styled.span`
  font-size: 12.5px;
  color: #8f8d85;
  padding-top: 3px;
`;

const Callout = styled.p`
  margin: 14px 0 18px;
  padding: 2px 0 2px 14px;
  border-left: 3px solid #e7e4db;
  color: #6f6d66;
  font-size: 14px;
  line-height: 1.5;
`;

const SectionTitle = styled.h4`
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const SectionBody = styled.p`
  margin: 0 0 4px;
  font-size: 13.5px;
  line-height: 1.5;
  color: #6f6d66;
`;

const FundRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 0;

  & + & {
    border-top: 1px solid #f1efe8;
  }
`;

const FundInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FundName = styled.div`
  font-size: 14.5px;
  font-weight: 600;
`;

const FundSub = styled.div`
  font-size: 12.5px;
  color: #8f8d85;
  margin-top: 3px;
`;

const FlagCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
`;

const FlagValue = styled.div`
  font-size: 14.5px;
  font-weight: 600;

  small {
    font-size: 11.5px;
    font-weight: 500;
    color: #8f8d85;
    margin-left: 3px;
  }
`;

const toneMap = {
  green: { dot: '#34c19a', text: '#1f9d74' },
  yellow: { dot: '#f2c94c', text: '#a98a1f' },
  pink: { dot: '#f26da7', text: '#e14e8b' },
};

const Pill = styled.span<{ tone: keyof typeof toneMap }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: ${(p) => toneMap[p.tone].text};

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${(p) => toneMap[p.tone].dot};
  }
`;

const EdgeBar = styled.div`
  position: absolute;
  right: -20px;
  top: 14px;
  bottom: 14px;
  width: 3px;
  border-radius: 99px;
  background: linear-gradient(180deg, #34c19a 0%, #f2c94c 55%, #f26da7 100%);
`;

/* ------------------------------------------------------------------ */
/*  Card 5 - performance chart                                         */
/* ------------------------------------------------------------------ */

const ChartWrap = styled.div`
  margin: 10px -4px 0;
`;

const StatRow = styled.div`
  display: flex;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #f1efe8;
`;

const Stat = styled.div`
  flex: 1;
  padding: 0 4px;

  & + & {
    border-left: 1px solid #f1efe8;
    padding-left: 14px;
  }
`;

const StatLabel = styled.div`
  font-size: 11.5px;
  color: #8f8d85;
  margin-bottom: 5px;
`;

const StatValue = styled.div<{ accent?: boolean }>`
  font-size: 15px;
  font-weight: 600;
  color: ${(p) => (p.accent ? '#1f9d74' : '#1c1c1a')};

  small {
    font-size: 11.5px;
    font-weight: 500;
    color: #8f8d85;
    margin-left: 2px;
  }
`;

/* ------------------------------------------------------------------ */
/*  Small SVG helpers                                                  */
/* ------------------------------------------------------------------ */

const Sparkline = ({ points, color }: { points: number[]; color: string }) => {
  const w = 64;
  const h = 30;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const stepX = w / (points.length - 1);
  const coords = points.map((p, i) => [
    i * stepX,
    h - 4 - ((p - min) / (max - min || 1)) * (h - 9),
  ]);
  const d = coords
    .map((c, i) => `${i === 0 ? 'M' : 'L'}${c[0].toFixed(1)},${c[1].toFixed(1)}`)
    .join(' ');

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <line
        x1="0"
        y1={h / 2}
        x2={w}
        y2={h / 2}
        stroke="#e3e0d8"
        strokeWidth="1"
        strokeDasharray="2 3"
      />
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      {coords.map((c, i) => (
        <circle key={i} cx={c[0]} cy={c[1]} r="2.4" fill="#fff" stroke={color} strokeWidth="1.5" />
      ))}
    </svg>
  );
};

const PerformanceChart = () => {
  const pts: Array<[number, number]> = [
    [10, 132],
    [82, 113],
    [154, 121],
    [226, 78],
    [298, 51],
  ];
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0]},${p[1]}`).join(' ');
  const area = `${line} L298,150 L10,150 Z`;
  const dotColors = ['#34c19a', '#34c19a', '#f2c94c', '#34c19a', '#34c19a'];
  const labels = ["Sep '25", "Nov '25", "Jan '26", "Mar '26", "May '26"];

  return (
    <svg width="100%" viewBox="0 0 340 162">
      <defs>
        <linearGradient id="etfFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#34c19a" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#34c19a" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* benchmark dashed line */}
      <line x1="6" y1="95" x2="304" y2="95" stroke="#dcd9d0" strokeWidth="1" strokeDasharray="3 4" />
      <text x="308" y="98" fontSize="9" fill="#a3a198">Benchmark</text>

      <path d={area} fill="url(#etfFill)" />
      <path d={line} fill="none" stroke="#34c19a" strokeWidth="1.6" strokeLinejoin="round" />
      {pts.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r="3.2" fill="#fff" stroke={dotColors[i]} strokeWidth="2" />
      ))}

      {labels.map((l, i) => (
        <text key={l} x={pts[i][0]} y="158" fontSize="9.5" fill="#a3a198" textAnchor="middle">
          {l}
        </text>
      ))}
    </svg>
  );
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const Comp = ({}) => {
  return (
    <Page>
      <Stack>
        {/* 1 - holdings breakdown */}
        <Card>
          <CardTitle>12 Index funds</CardTitle>
          <LegendRow>
            <LegendItem>
              <strong>7</strong> Equity
            </LegendItem>
            <LegendItem>
              <strong>3</strong> International
            </LegendItem>
            <LegendItem>
              <strong>2</strong> Bond
            </LegendItem>
          </LegendRow>
          <SegBar>
            <Seg w={58.3} c="#34c19a" />
            <Seg w={25} c="#f2c94c" />
            <Seg w={16.7} c="#f26da7" />
          </SegBar>
        </Card>

        {/* 2 - allocation drift gauge */}
        <Card>
          <RowBetween>
            <CardTitle>Allocation drift</CardTitle>
            <div>
              <BigNumber>
                +3.4<small>%</small>
              </BigNumber>
              <BigCaption>Above target mix</BigCaption>
            </div>
          </RowBetween>
          <Gauge>
            <Track>
              <Band l={42} w={16} />
              <Marker at={67} />
            </Track>
          </Gauge>
          <TickRow>
            <span>-10%</span>
            <span>Target</span>
            <span>+10%</span>
          </TickRow>
        </Card>

        {/* 3 - portfolio score */}
        <Card>
          <RowBetween>
            <CardTitle style={{ textTransform: 'lowercase' }}>portfolio score</CardTitle>
            <div>
              <BigNumber>
                82<small>/100</small>
              </BigNumber>
              <BigCaption>Well diversified</BigCaption>
            </div>
          </RowBetween>
          <Gauge>
            <Track>
              <Band l={0} w={82} c="#f2c94c" />
              <Marker at={82} />
            </Track>
          </Gauge>
          <TickRow>
            <span>0</span>
            <span>50</span>
            <span>100</span>
          </TickRow>
        </Card>

        {/* 4 - review / action plan */}
        <Card>
          <RowBetween>
            <CardTitle>Your portfolio review</CardTitle>
            <DateText>Jul 16, 2026</DateText>
          </RowBetween>
          <Callout>
            We flagged 2 holdings that may be quietly costing you. Here is what we observed:
          </Callout>
          <SectionTitle>Elevated fund fees</SectionTitle>
          <SectionBody>
            Two funds carry expense ratios well above their category average:
          </SectionBody>

          <FundRow>
            <FundInfo>
              <FundName>Actively Managed Growth</FundName>
              <FundSub>U.S. large cap</FundSub>
            </FundInfo>
            <FlagCol>
              <Pill tone="pink">High fee</Pill>
              <FlagValue>
                0.73<small>% exp.</small>
              </FlagValue>
            </FlagCol>
            <Sparkline points={[12, 18, 14, 22, 17, 25]} color="#f26da7" />
            <EdgeBar />
          </FundRow>

          <FundRow>
            <FundInfo>
              <FundName>Legacy Mutual Fund</FundName>
              <FundSub>U.S. large cap</FundSub>
            </FundInfo>
            <FlagCol>
              <Pill tone="yellow">Index overlap</Pill>
              <FlagValue>
                0.68<small>% exp.</small>
              </FlagValue>
            </FlagCol>
            <Sparkline points={[15, 13, 18, 16, 21, 19]} color="#f2c94c" />
            <EdgeBar />
          </FundRow>
        </Card>

        {/* 5 - fund performance */}
        <Card>
          <RowBetween>
            <CardTitle>Total Market Index Fund</CardTitle>
            <Pill tone="green">On track</Pill>
          </RowBetween>
          <ChartWrap>
            <PerformanceChart />
          </ChartWrap>
          <StatRow>
            <Stat>
              <StatLabel>YTD return</StatLabel>
              <StatValue >+12.4%</StatValue>
            </Stat>
            <Stat>
              <StatLabel>Expense ratio</StatLabel>
              <StatValue>0.03%</StatValue>
            </Stat>
            <Stat>
              <StatLabel>Data source</StatLabel>
              <StatValue>
                Market<small>close</small>
              </StatValue>
            </Stat>
          </StatRow>
        </Card>
      </Stack>
    </Page>
  );
};

export default Comp;

